// src/Register.js
// ============================================================
// ScholarSync AI — Registration Page
// ============================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { API_URL } from './config';
import { useAuth } from './AuthContext';

export default function Register() {
  const [fullName, setFullName]               = useState('');
  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole]                       = useState('student');
  const [error, setError]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname;
  const [searchParams] = useSearchParams();
  const inviteStudentId = searchParams.get('invite');
  const isInvite = !!inviteStudentId;

  // Invite mode: pre-fill name/email from student record and lock to student role
  useEffect(() => {
    if (!inviteStudentId) return;
    setRole('student');
    // S76: no credential before signup — pre-fill is best-effort; if the API
    // refuses, the student simply types their name and email.
    fetch(`${API_URL}/api/students/${inviteStudentId}`)
      .then(r => (r.ok ? r.json() : {}))
      .then(student => {
        if (student.full_name) setFullName(student.full_name);
        if (student.email) setEmail(student.email);
      })
      .catch(err => console.error('Failed to load invite:', err));
  }, [inviteStudentId]);
  const { login, isAuthenticated, studentId, loading: authLoading } = useAuth();

  // If already authenticated, redirect based on role
  const { user: authUser } = useAuth();
      useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (isInvite) return;
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else if (authUser?.role === 'parent') {
        navigate('/parent/dashboard', { replace: true });
      } else if (studentId) {
        navigate(`/dashboard/${studentId}`, { replace: true });
      } else {
        navigate('/profile/new', { replace: true });
      }
    }
    }, [authLoading, isAuthenticated, studentId, authUser, navigate, redirectTo, isInvite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // --- Client-side validation ---
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName,
          role,
          agreed_to_beta_terms: true,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Registration failed.');
        setLoading(false);
        return;
      }

            // Invite mode: link student record to the new user account
      if (isInvite && data.user?.id) {
        try {
          const linkRes = await fetch(`${API_URL}/api/students/${inviteStudentId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.token}` },
            body: JSON.stringify({ user_id: data.user.id }),
          });
          if (!linkRes.ok) {
            const linkErr = await linkRes.json().catch(() => ({}));
            console.error('Failed to link student:', linkErr);
            setError('Your account was created, but we couldn\'t connect it to your parent\'s plan. Please ask your parent to contact support.');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Failed to link student:', err);
          setError('Your account was created, but we couldn\'t connect it to your parent\'s plan. Please ask your parent to contact support.');
          setLoading(false);
          return;
        }
      }

      // Auto-login after registration
      login(data.token, data.user);

            // Redirect: saved destination (e.g. /checkout) > role-aware default
      if (isInvite) {
        navigate('/profile/new');
      } else if (redirectTo) {
        navigate(redirectTo);
      } else if (role === 'parent') {
        navigate('/parent/dashboard');
      } else {
        navigate('/profile/new');
      }
    } catch (err) {
      setError('Network error — please check your connection.');
      setLoading(false);
    }
  };
    if (authLoading) return null;

  if (isAuthenticated && isInvite) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <img src="/YSF_logo_horizontal_navy.png" alt="Your Scholarship Finder" style={{ maxWidth: '220px', marginBottom: '24px' }} />
          <h1 style={styles.title}>This Link Is for Your Student</h1>
          <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', marginBottom: '12px' }}>
            You're currently logged in as a parent. This invite link is for your child to create their own account and password.
          </p>
          <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
            Have your child open this link in their own browser (or a private/incognito window). They'll set up their own login from there.
          </p>
          <button onClick={() => navigate('/parent/dashboard')} style={styles.button}>
            Back to Parent Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>{isInvite ? 'Accept Your Invitation' : 'Create Your Account'}</h1>
        <p style={styles.subtitle}>
          {isInvite
            ? 'Your parent has set up a Your Scholarship Finder account for you. Create a password to get started.'
            : 'Join Your Scholarship Finder to find scholarships matched to your profile.'}
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
                   
          {/* ── Account Type ── */}
          {!isInvite && <div style={styles.field}>
            <label style={styles.label}>I am a…</label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setRole('student')}
                style={{
                  ...styles.roleButton,
                  ...(role === 'student' ? styles.roleButtonActive : {}),
                }}
              >
                🎓 Student
              </button>
              <button
                type="button"
                onClick={() => setRole('parent')}
                style={{
                  ...styles.roleButton,
                  ...(role === 'parent' ? styles.roleButtonActive : {}),
                }}
              >
                👪 Parent
              </button>
            </div>
          </div>}

          <div style={styles.field}>
            <label style={styles.label}>Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              style={styles.input}
              placeholder="Your full name"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
              placeholder="you@example.com"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={styles.input}
              placeholder="At least 8 characters"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={styles.input}
              placeholder="Re-enter your password"
            />
          </div>


          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              opacity: loading ? 0.5 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <p style={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>Log in</Link>
        </p>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F3F4F6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '20px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '28px',
  },
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    padding: '12px',
    color: '#DC2626',
    fontSize: '13px',
    marginBottom: '20px',
  },
  field: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '12px',
    background: '#111827',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
  },
  footer: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#6B7280',
    marginTop: '20px',
  },
  link: {
    color: '#2563EB',
    textDecoration: 'none',
    fontWeight: '600',
  },
  roleButton: {
    flex: 1,
    padding: '10px',
    border: '2px solid #D1D5DB',
    borderRadius: '8px',
    background: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    color: '#6B7280',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  roleButtonActive: {
    borderColor: '#111827',
    color: '#111827',
    background: '#F9FAFB',
  },
};
